import { useCallback, RefObject } from 'react'

interface UsePreventPointerDownIfInsideOptions {
  overridePointerEvents?: boolean
  pointerEventsValue?: string
}

interface PointerDownOutsideDetail {
  originalEvent: PointerEvent
}

type PointerDownOutsideEvent = CustomEvent<PointerDownOutsideDetail>

/**
 * A generic hook to intercept pointer down events and prevent their propagation
 * if they occur inside the target element.
 *
 * This hook was initially created to address a specific limitation in the Radix UI Dialog/Sheet components.
 * The default behavior of the Dialog component, particularly in the implementation of
 * [DialogContentImpl](https://github.com/radix-ui/primitives/blob/main/packages/react/dialog/src/dialog.tsx#L278),
 * enforces a hardcoded behavior for `disableOutsidePointerEvents`. This behavior disables pointer
 * events outside the dialog by setting the `pointer-events` CSS property to `none`. While this is useful
 * for modals that need to trap all user interactions, it can be restrictive in scenarios where interactions
 * with elements outside the dialog are required. Since the `disableOutsidePointerEvents` prop cannot be
 * customized, this hook provides a way to override the pointer events for elements outside the dialog.
 *
 * The hook retrieves the bounding rectangle of the target element and adjusts it to absolute coordinates
 * using the window's scroll position. This is necessary because in modal scenarios, when a user clicks outside
 * the visible modal, the event's target often defaults to the entire HTML document rather than the specific
 * element that triggered the event. By using the absolute coordinates, the hook accurately determines whether
 * the pointer event (based on its clientX and clientY values) occurred inside or outside the target element.
 *
 * Although originally designed for this specific use case, the hook is generalized to be reusable in other
 * scenarios where similar behavior is needed.
 *
 * @param targetRef - A ref to the element to monitor for pointer down events.
 * @param options - Optional settings to customize the behavior:
 *   - `overridePointerEvents`: Whether to override the `pointer-events` CSS property (default: `true`).
 *   - `pointerEventsValue`: The value to set for the `pointer-events` property when overriding (default: `'auto'`).
 * @returns A memoized event handler for pointer down events.
 */
export const usePreventPointerDownIfInside = (
  targetRef: RefObject<HTMLElement>,
  {
    overridePointerEvents = true,
    pointerEventsValue = 'auto',
  }: UsePreventPointerDownIfInsideOptions = {}
) => {
  return useCallback(
    (event: PointerDownOutsideEvent) => {
      const targetEl = targetRef?.current
      if (!targetEl) return

      const rect = targetEl.getBoundingClientRect()

      const absoluteRect = {
        left: rect.left + window.scrollX,
        top: rect.top + window.scrollY,
        right: rect.right + window.scrollX,
        bottom: rect.bottom + window.scrollY,
      }

      const { clientX, clientY } = event.detail.originalEvent

      const isInside =
        clientX >= absoluteRect.left &&
        clientX <= absoluteRect.right &&
        clientY >= absoluteRect.top &&
        clientY <= absoluteRect.bottom

      if (isInside) {
        if (overridePointerEvents) {
          targetEl.style.pointerEvents = pointerEventsValue
        }
        event.preventDefault()
      }
    },
    [targetRef, overridePointerEvents, pointerEventsValue]
  )
}
